import HotTable from '@handsontable/react-wrapper';
import Handsontable from 'handsontable';
import { CellValue, RangeType } from 'handsontable/common';
import { DropdownEditor } from 'handsontable/editors';
import { ColumnSettings } from 'handsontable/settings';
import { toNumber } from 'lodash';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { UpdateBudgetModel, GetCustomerSettingsResponse } from '../../../../Generated/Commodity-Management-Api';
import { months, monthToNumber } from '../../../../Helpers';
import { StoneXNumber } from '../../../StoneXMui';
import { BudgetRow } from './UpdateBudgetsPage/BudgetTypes';
import { useApplicationSelector, RootState } from "../../../../Redux/ReduxStore";

interface EditBudgetsTableProps {
  budgets: BudgetRow[];
  isSaving: boolean;
  displayPrecision?: number;
}

export interface EditBudgetsTableRef {
  isValid: () => Promise<boolean>;
  getBudgets: () => { budgets: UpdateBudgetModel[] };
}

const EditBudgetsTable = forwardRef<EditBudgetsTableRef, EditBudgetsTableProps>((props, ref) => {
  const { budgets, isSaving, displayPrecision } = props;

  const hotRef = useRef<any>(null);

  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

  const [columnsToHide, setColumnsToHide] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const hideContractDate = useMemo(() => !customerSettings.positionContractDates, []);

  useImperativeHandle(ref, () => ({
    isValid,
    getBudgets,
  }));

  async function isValid(): Promise<boolean> {
    const hotInstance = hotRef.current.hotInstance;

    return new Promise((resolve) => {
      hotInstance.validateCells((valid: boolean) => {
        console.log('validation result', valid);
        resolve(valid);
      });
    });
  }

  function getBudgets(): { budgets: UpdateBudgetModel[] } {
    const hotInstance = hotRef.current.hotInstance; 

    var updateBudgetsModel = {
      budgets: budgets.map((budget, index) => {
        // Find corresponding row in hot table data
        let rowIndex = index; // Default to same index
        if (budget.budgetId) {
          // If budget has an ID, find its row in the hot table
          const data = hotInstance.getData();
          rowIndex = data.findIndex((row: any[]) => 
            hotInstance.getDataAtRowProp(data.indexOf(row), 'budgetId') === budget.budgetId
          );
          if (rowIndex === -1) rowIndex = index; // Fallback to index if not found
        }

        const budgetYear = hotInstance.getDataAtRowProp(rowIndex, 'budgetDate.year') ?? undefined;
        const budgetMonth = monthToNumber(hotInstance.getDataAtRowProp(rowIndex, 'budgetDate.month')) ?? undefined;
        const budgetDate = {year: budgetYear, month: budgetMonth};

        const contractYear = hotInstance.getDataAtRowProp(rowIndex, 'contractDate.year') ?? undefined;
        const contractMonth = monthToNumber(hotInstance.getDataAtRowProp(rowIndex, 'contractDate.month')) ?? undefined;
        const contractDate = contractYear && contractMonth ? {year: contractYear, month: contractMonth} : undefined;

        const volume = hotInstance.getDataAtRowProp(rowIndex, 'volume') ? 
          toNumber(hotInstance.getDataAtRowProp(rowIndex, 'volume')) : undefined;

        const budgetPrices = budget.budgetPrices?.map((component, index) => ({
          budgetPriceId: component.budgetPriceId ?? 0,
          componentType: component.componentType,
          price: hotInstance.getDataAtRowProp(rowIndex, `budgetPrices.${index}.price`) 
            ? toNumber(hotInstance.getDataAtRowProp(rowIndex, `budgetPrices.${index}.price`)) 
            : undefined,
          unit: budget.unit
        }));
        
        return {
          budgetId: budget.budgetId,
          pricingStrategyId: budget.pricingStrategyId,
          commodityId: budget.commodityId,
          locationId: budget.locationId,
          counterpartyId: budget.counterpartyId,
          budgetRoundId: budget.budgetRoundId,
          unit: budget.unit,
          currency: budget.currency,
          budgetDate,
          contractDate,
          volume,
          budgetPrices,
        } as UpdateBudgetModel;
      }).filter(b=>b.volume != 0)
    };

    return updateBudgetsModel;
  };

  function updateColumnsToHide() {
    const hotInstance = hotRef.current?.hotInstance;

    if (!hotInstance) {
      setColumnsToHide([0, 1, 2, 3, 4, 5, 6]);
      return;
    }

    let hidden = [0, 1, 2, 3, 4, 5, 6];

    if (hideContractDate) {
      const contractYearTypeColIndex = hotInstance.propToCol('contractDate.year');
      const contractMonthTypeColIndex = hotInstance.propToCol('contractDate.month');
      hidden.push(...[contractYearTypeColIndex, contractMonthTypeColIndex]);
    }

    setColumnsToHide(hidden);
  }

  function beforePaste(data: CellValue[][], coords: RangeType[]) {
    const monthMap: { [key: string]: number } = {
      january: 1, jan: 1,
      february: 2, feb: 2,
      march: 3, mar: 3,
      april: 4, apr: 4,
      may: 5,
      june: 6, jun: 6,
      july: 7, jul: 7,
      august: 8, aug: 8,
      september: 9, sep: 9,
      october: 10, oct: 10,
      november: 11, nov: 11,
      december: 12, dec: 12,
    };

    const hotInstance = hotRef.current.hotInstance;
    const budgetMonthCol = hotInstance.propToCol('budgetDate.month');
    const contractMonthCol = hotInstance.propToCol('contractDate.month');
    const volumeCol = hotInstance.propToCol('volume');
    const componentPriceCols = budgets[0]?.budgetPrices?.map((_, index) => hotInstance.propToCol(`budgetPrices.${index}.price`)) ?? [];

    coords.forEach((range) => {
      const startCol = range.startCol;

      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < data[i].length; j++) {
          const value = String(data[i][j]).toLowerCase();

          // Process month columns
          if (startCol === budgetMonthCol || startCol === contractMonthCol) {
            if (monthMap[value] !== undefined) {
              data[i][j] = months[monthMap[value] - 1];
            }
            const monthNum = parseInt(value);
            if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
              data[i][j] = months[monthNum - 1];
            }
          }

          // Process numeric columns
          if (startCol === volumeCol || componentPriceCols.includes(startCol)) {
            data[i][j] = value.replace(/[^0-9.]/g, '');
          }
        }
      }
    });
  }

  const cells = (row: any, col: any, prop: any) => {
    const cellProperties = {} as any;
    const hotInstance = hotRef.current.hotInstance;

    if (isSaving) {
      cellProperties.readOnly = true;
      return cellProperties;
    }

    return cellProperties;
  };

  class CustomDropdown extends DropdownEditor {
    limitDropdownIfNeeded(spaceAvailable: any, dropdownHeight: any): void {
      super.limitDropdownIfNeeded(spaceAvailable, dropdownHeight);
      const newHeight = (this.cellProperties?.source?.length ?? 0) * 40;
      this.htEditor.updateSettings({ height: newHeight + 2, width: '100%' });
    }

    // Override flipDropdown to prevent negative top
    flipDropdown(dropdownHeight: number) {
      // Do not set negative top, always open below
      const dropdownStyle = this.htEditor.rootElement.style;
      dropdownStyle.position = 'absolute';
      dropdownStyle.top = '0px';
    }
  }

  const volumeRenderer = function (instance: any, TD: any, row: any, col: any, prop: any, value: any, cellProperties: any) {
    if (!TD.__root) {
      TD.__root = createRoot(TD);
    }
    const element = <StoneXNumber number={value} className={cellProperties.readOnly ? 'htDimmed' : undefined}/>;
    TD.__root.render(element);
  };

  const priceRenderer = function (_instance: any, TD: any, _row: any, _col: any, _prop: any, value: any, cellProperties: any) {
    if (!TD.__root) {
      TD.__root = createRoot(TD);
    }
    const element = <StoneXNumber number={value} decimals={displayPrecision ?? 3} allowMoreDecimals ifNoNumber='' className={cellProperties.readOnly ? 'htDimmed' : undefined}/>;
    TD.__root.render(element);
  };

  function yearValidator(value: CellValue, callback: (valid: boolean) => void) {
    const year = parseInt(value as string, 10);
    const isValidYear = !isNaN(year) && year > 0;

    if (value === null || value === undefined || value === '' || !isValidYear) {
      callback(false); // Invalid
    } else {
      callback(true); // Valid
    }
  }

  function numericValidator(value: CellValue, callback: (valid: boolean) => void) {
    const number = parseFloat(value as string);
    const isValidVolume = !isNaN(number) && number >= 0;

    if (value === null || value === undefined || value === '' || !isValidVolume) {
      callback(false); // Invalid
    } else {
      callback(true); // Valid
    }
  }

  // Register custom editor and validators
  Handsontable.editors.registerEditor('customDropdown', CustomDropdown);
  Handsontable.validators.registerValidator('yearValidator', yearValidator);
  Handsontable.validators.registerValidator('numericValidator', numericValidator);
  Handsontable.renderers.registerRenderer('priceRenderer', priceRenderer);
  Handsontable.renderers.registerRenderer('volumeRenderer', volumeRenderer);

  const columns: ColumnSettings[] = useMemo(
    () => [
      {
        data: 'budgetId',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'pricingStrategyId',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'commodityId',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'counterpartyId',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'locationId',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'unit',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'currency',
        readOnly: true,
        className: 'htHidden',
      },
      {
        data: 'budgetDate.month',
        readOnly: true,
        validator: (value, callback) => callback(!!value && months.includes(value)),
      },
      {
        data: 'budgetDate.year',
        className: 'numeric',
        readOnly: true,
        validator: 'yearValidator',
      },
      {
        data: 'contractDate.month',
        type: 'dropdown',
        editor: 'customDropdown',
        source: months,
        validator: (value, callback) => {
          if (hideContractDate) {
            return callback(true);
          }
          callback(!!value && months.includes(value));
        },
      },
      {
        data: 'contractDate.year',
        className: 'numeric',
        validator: (value, callback) => {
          if (hideContractDate) {
            return callback(true);
          }
          yearValidator(value, (isValid) => callback(!!value && isValid));
        },
      },
      {
        data: 'volume',
        className: 'numeric',
        validator: 'numericValidator',
        renderer: 'volumeRenderer',
      },
      ...(budgets[0]?.budgetPrices?.map((component, index) => ({
        data: `budgetPrices.${index}.price`,
        className: 'numeric',
        validator: 'numericValidator',
        renderer: 'priceRenderer',
      })) ?? []),
    ],
    [budgets, hideContractDate],
  );

  return (
    <HotTable
      ref={hotRef}
      data={budgets.map((budgets) => ({
        ...budgets,
        budgetDate: {
          ...budgets.budgetDate,
          month: budgets.budgetDate?.month ? months[budgets.budgetDate.month - 1] : null,
        },
        contractDate: {
          ...budgets.contractDate,
          month: budgets.contractDate?.month ? months[budgets.contractDate.month - 1] : null,
        },
      }))}
      stretchH="all"
      width="100%"
      height="auto"
      colHeaders={[
        'Budget Id',
        'Budget Pricing Strategy Id',
        'Commodity Id',
        'Counterparty Id',
        'Location Id',
        'Unit',
        'Currency',
        'Budget Month',
        'Budget Year',
        'Contract Month',
        'Contract Year',
        'Quantity',
        ...(budgets[0]?.budgetPrices?.map((component: any) => component.componentType!) ?? []),
      ]}
      columns={columns}
      autoWrapRow={true}
      autoWrapCol={true}
      rowHeights={40}
      columnHeaderHeight={48}
      minSpareRows={0}
      hiddenColumns={{
        columns: columnsToHide,
        indicators: false,
        copyPasteEnabled: false,
      }}
      cells={cells}
      init={updateColumnsToHide}
      beforePaste={beforePaste}
      licenseKey="29ae0-f35ad-79c41-2472d-2c70d"
    />
  );
});

export default EditBudgetsTable;